import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Storefront, Plus, Power, Robot, Copy, Trash, PencilSimple, X, CheckCircle } from "@phosphor-icons/react";

const LIST_FIELDS = [
  ["customer_problems", "Customer Problems Solved"],
  ["features", "Features"],
  ["benefits", "Benefits"],
  ["approved_claims", "Approved Sales Claims"],
  ["approved_promotions", "Approved Promotions"],
  ["approved_discounts", "Approved Discounts"],
  ["talking_points", "Sales Talking Points"],
  ["qualification_questions", "Qualification Questions"],
  ["required_info", "Required Customer Information"],
  ["cross_sell", "Cross-Sell Product Keys"],
  ["upsell", "Upsell Product Keys"],
];

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const blank = (schema) => ({
  name: "", product_key: "", category: schema?.categories?.[0] || "other", description: "",
  audience: "both", target_customer: "", icp: { industry: "", company_size: "", criteria: "" },
  pricing_model: "one_time", price_note: "", price_cents: 0, currency: "USD",
  availability: "active", countries: [], excluded_countries: [], eligibility_note: "",
  conversion_goal: "LEAD", checkout_method: "none", fulfillment_method: "none",
  human_approval_required: false, ai_sales_enabled: true, status: "active",
  knowledge_base: "", objections: [],
  ...Object.fromEntries(LIST_FIELDS.map(([k]) => [k, []])),
});

export default function AdminSalesCatalog() {
  const [schema, setSchema] = useState(null);
  const [products, setProducts] = useState([]);
  const [tenant, setTenant] = useState("");
  const [editing, setEditing] = useState(null); // product object or null
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: sc }, { data: pr }] = await Promise.all([
        api.get("/sales-catalog/schema"), api.get("/sales-catalog/products"),
      ]);
      setSchema(sc.schema); setProducts(pr.products); setTenant(pr.tenant_id);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(blank(schema));
  const startEdit = (p) => setEditing({ ...blank(schema), ...p });

  const toggleAi = async (p) => {
    try {
      await api.post(`/sales-catalog/products/${p.product_key}/ai-sales`, { enabled: !p.ai_sales_enabled });
      toast.success(p.ai_sales_enabled ? "SI Sales disabled" : "SI Sales enabled");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (p, status) => {
    try { await api.post(`/sales-catalog/products/${p.product_key}/status`, { status }); toast.success(`Set ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (p) => {
    try { await api.post(`/sales-catalog/products/${p.product_key}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? It stops being sellable.`)) return;
    try { await api.delete(`/sales-catalog/products/${p.product_key}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Product name is required");
    setBusy(true);
    try {
      if (editing.product_key && products.some((p) => p.product_key === editing.product_key)) {
        await api.put(`/sales-catalog/products/${editing.product_key}`, editing);
      } else {
        await api.post("/sales-catalog/products", editing);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const sellableCount = useMemo(
    () => products.filter((p) => p.status === "active" && p.availability === "active" && p.ai_sales_enabled).length,
    [products]);

  if (!schema) return <div className="py-16 text-center text-muted-foreground" data-testid="catalog-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-sales-catalog">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight">
            <Storefront size={28} weight="fill" /> Universal Sales Catalog
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            One catalog powers the whole SI Sales engine. Add a product, fill its structured sales data, enable SI Sales —
            the engine can then discover, recommend, sell and cross-sell it with no rebuild.
            <span className="ml-1 font-medium text-foreground">Tenant: {tenant}</span>
          </p>
        </div>
        <button data-testid="catalog-new" onClick={startNew}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
          <Plus size={16} weight="bold" /> Add product / service
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="catalog-stats">
        <Stat label="Products" v={products.length} />
        <Stat label="SI-sellable now" v={sellableCount} good />
        <Stat label="Active" v={products.filter((p) => p.status === "active").length} />
        <Stat label="Archived" v={products.filter((p) => p.status === "archived").length} />
      </div>

      <div className="grid gap-3">
        {products.map((p) => (
          <div key={p.product_key} data-testid={`catalog-row-${p.product_key}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-card p-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{p.name}</span>
                <Badge>{p.category}</Badge>
                <Badge tone="sky">{p.conversion_goal}</Badge>
                {p.status !== "active" && <Badge tone="muted">{p.status}</Badge>}
                {p.ai_sales_enabled && p.status === "active" && p.availability === "active"
                  ? <Badge tone="emerald"><CheckCircle size={12} weight="fill" className="mr-0.5 inline" /> sellable</Badge>
                  : <Badge tone="muted">not sellable</Badge>}
              </div>
              <p className="mt-1 truncate text-xs text-muted-foreground">{p.product_key} · {p.description || "—"}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <IconBtn testid={`catalog-ai-${p.product_key}`} title={p.ai_sales_enabled ? "Disable SI Sales" : "Enable SI Sales"}
                active={p.ai_sales_enabled} onClick={() => toggleAi(p)}><Robot size={16} weight="fill" /></IconBtn>
              <IconBtn testid={`catalog-power-${p.product_key}`} title={p.status === "active" ? "Deactivate" : "Activate"}
                active={p.status === "active"} onClick={() => setStatus(p, p.status === "active" ? "inactive" : "active")}><Power size={16} weight="bold" /></IconBtn>
              <IconBtn testid={`catalog-edit-${p.product_key}`} title="Edit" onClick={() => startEdit(p)}><PencilSimple size={16} weight="bold" /></IconBtn>
              <IconBtn testid={`catalog-dup-${p.product_key}`} title="Duplicate" onClick={() => duplicate(p)}><Copy size={16} weight="bold" /></IconBtn>
              <IconBtn testid={`catalog-archive-${p.product_key}`} title="Archive" danger onClick={() => archive(p)}><Trash size={16} weight="bold" /></IconBtn>
            </div>
          </div>
        ))}
        {products.length === 0 && <p className="rounded-2xl border border-white/10 bg-card p-8 text-center text-sm text-muted-foreground">No products yet. Add your first sellable product.</p>}
      </div>

      {editing && (
        <Editor schema={schema} value={editing} setValue={setEditing} onSave={save} onClose={() => setEditing(null)} busy={busy}
          isNew={!products.some((p) => p.product_key === editing.product_key)} />
      )}
    </div>
  );
}

function Editor({ schema, value, setValue, onSave, onClose, busy, isNew }) {
  const set = (k, v) => setValue((s) => ({ ...s, [k]: v }));
  const setIcp = (k, v) => setValue((s) => ({ ...s, icp: { ...s.icp, [k]: v } }));
  const setObj = (i, k, v) => setValue((s) => { const o = [...(s.objections || [])]; o[i] = { ...o[i], [k]: v }; return { ...s, objections: o }; });
  const addObj = () => setValue((s) => ({ ...s, objections: [...(s.objections || []), { objection: "", response: "" }] }));
  const rmObj = (i) => setValue((s) => ({ ...s, objections: s.objections.filter((_, x) => x !== i) }));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" data-testid="catalog-editor" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-white/10 bg-background p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">{isNew ? "New product / service" : `Edit · ${value.name}`}</h2>
          <button data-testid="catalog-editor-close" onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10"><X size={18} /></button>
        </div>

        <div className="space-y-5">
          <Group title="Basics">
            <Txt testid="ed-name" label="Name*" value={value.name} onChange={(v) => set("name", v)} />
            <Sel testid="ed-category" label="Category" value={value.category} opts={schema.categories} onChange={(v) => set("category", v)} />
            <Area testid="ed-description" label="Description" value={value.description} onChange={(v) => set("description", v)} full />
            <Sel testid="ed-audience" label="Audience" value={value.audience} opts={schema.audiences} onChange={(v) => set("audience", v)} />
            <Txt testid="ed-target" label="Target customer" value={value.target_customer} onChange={(v) => set("target_customer", v)} />
          </Group>

          <Group title="Ideal customer profile (ICP)">
            <Txt testid="ed-icp-industry" label="Industry" value={value.icp?.industry} onChange={(v) => setIcp("industry", v)} />
            <Txt testid="ed-icp-size" label="Company size" value={value.icp?.company_size} onChange={(v) => setIcp("company_size", v)} />
            <Area testid="ed-icp-criteria" label="Other criteria" value={value.icp?.criteria} onChange={(v) => setIcp("criteria", v)} full />
          </Group>

          <Group title="Pricing & availability">
            <Sel testid="ed-pricing-model" label="Pricing model" value={value.pricing_model} opts={schema.pricing_models} onChange={(v) => set("pricing_model", v)} />
            <Txt testid="ed-price-note" label="Price note (shown to customer)" value={value.price_note} onChange={(v) => set("price_note", v)} />
            <Num testid="ed-price-cents" label="Reference price (cents)" value={value.price_cents} onChange={(v) => set("price_cents", v)} />
            <Txt testid="ed-currency" label="Currency" value={value.currency} onChange={(v) => set("currency", v)} />
            <Sel testid="ed-availability" label="Availability" value={value.availability} opts={["active", "inactive"]} onChange={(v) => set("availability", v)} />
            <Txt testid="ed-countries" label="Countries (ISO2, comma — empty = global)" value={(value.countries || []).join(",")} onChange={(v) => set("countries", v.split(",").map((x) => x.trim()).filter(Boolean))} />
            <Txt testid="ed-excluded" label="Excluded countries (ISO2, comma)" value={(value.excluded_countries || []).join(",")} onChange={(v) => set("excluded_countries", v.split(",").map((x) => x.trim()).filter(Boolean))} />
            <Area testid="ed-eligibility" label="Eligibility note" value={value.eligibility_note} onChange={(v) => set("eligibility_note", v)} full />
          </Group>

          <Group title="Conversion & fulfillment">
            <Sel testid="ed-goal" label="Conversion goal" value={value.conversion_goal} opts={schema.conversion_goals} onChange={(v) => set("conversion_goal", v)} />
            <Sel testid="ed-checkout" label="Checkout method" value={value.checkout_method} opts={schema.checkout_methods} onChange={(v) => set("checkout_method", v)} />
            <Sel testid="ed-fulfillment" label="Fulfillment method" value={value.fulfillment_method} opts={schema.fulfillment_methods} onChange={(v) => set("fulfillment_method", v)} />
          </Group>

          <Group title="Sales knowledge (approved content only)" cols={1}>
            {LIST_FIELDS.map(([k, label]) => (
              <Area key={k} testid={`ed-${k}`} label={`${label} (one per line)`} value={toLines(value[k])} onChange={(v) => set(k, fromLines(v))} full />
            ))}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Objections & approved responses</span>
                <button data-testid="ed-add-objection" onClick={addObj} className="text-xs font-semibold text-primary hover:underline">+ Add</button>
              </div>
              <div className="space-y-2">
                {(value.objections || []).map((o, i) => (
                  <div key={i} className="rounded-xl border border-white/10 p-2" data-testid={`ed-objection-${i}`}>
                    <input data-testid={`ed-objection-q-${i}`} value={o.objection} placeholder="Objection" onChange={(e) => setObj(i, "objection", e.target.value)}
                      className="mb-1 w-full rounded-lg border border-white/10 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary" />
                    <div className="flex gap-2">
                      <input data-testid={`ed-objection-r-${i}`} value={o.response} placeholder="Approved response" onChange={(e) => setObj(i, "response", e.target.value)}
                        className="w-full rounded-lg border border-white/10 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary" />
                      <button onClick={() => rmObj(i)} className="rounded-lg px-2 text-destructive hover:bg-destructive/10"><Trash size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Area testid="ed-kb" label="Knowledge base" value={value.knowledge_base} onChange={(v) => set("knowledge_base", v)} full />
          </Group>

          <Group title="Controls">
            <Sel testid="ed-status" label="Status" value={value.status} opts={["active", "inactive", "archived"]} onChange={(v) => set("status", v)} />
            <Check testid="ed-human-approval" label="Require human approval to send/close" value={value.human_approval_required} onChange={(v) => set("human_approval_required", v)} />
            <Check testid="ed-ai-enabled" label="SI Sales enabled" value={value.ai_sales_enabled} onChange={(v) => set("ai_sales_enabled", v)} />
          </Group>
        </div>

        <div className="sticky bottom-0 mt-6 flex gap-2 bg-background pt-3">
          <button data-testid="catalog-save" onClick={onSave} disabled={busy}
            className="flex-1 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
            {busy ? "Saving…" : "Save product"}
          </button>
          <button onClick={onClose} className="rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold hover:border-primary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- small primitives ---------- */
const Stat = ({ label, v, good }) => (
  <div className="rounded-2xl border border-white/10 bg-card p-4">
    <p className={`font-display text-2xl font-semibold ${good ? "text-emerald-500" : ""}`}>{v}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);
const Badge = ({ children, tone }) => {
  const map = { sky: "bg-sky-500/15 text-sky-400", emerald: "bg-emerald-500/15 text-emerald-400", muted: "bg-white/10 text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${map[tone] || "bg-primary/15 text-primary"}`}>{children}</span>;
};
const IconBtn = ({ children, onClick, title, active, danger, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className={`rounded-full border p-2 transition-colors ${danger ? "border-white/10 text-muted-foreground hover:border-destructive hover:text-destructive"
      : active ? "border-primary bg-primary/15 text-primary" : "border-white/10 text-muted-foreground hover:border-primary"}`}>{children}</button>
);
const Group = ({ title, children, cols = 2 }) => (
  <div className="rounded-2xl border border-white/10 bg-card p-4">
    <h3 className="mb-3 font-semibold">{title}</h3>
    <div className={`grid gap-3 ${cols === 1 ? "" : "sm:grid-cols-2"}`}>{children}</div>
  </div>
);
const Lbl = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}
  </label>
);
const inputCls = "w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const Txt = ({ label, value, onChange, testid, full }) => (
  <Lbl label={label} full={full}><input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></Lbl>
);
const Num = ({ label, value, onChange, testid }) => (
  <Lbl label={label}><input data-testid={testid} type="number" value={value ?? 0} onChange={(e) => onChange(parseInt(e.target.value || 0, 10))} className={inputCls} /></Lbl>
);
const Area = ({ label, value, onChange, testid, full }) => (
  <Lbl label={label} full={full}><textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} /></Lbl>
);
const Sel = ({ label, value, opts, onChange, testid }) => (
  <Lbl label={label}>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </Lbl>
);
const Check = ({ label, value, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm font-medium">
    <input data-testid={testid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-5 w-9 accent-primary" /> {label}
  </label>
);
